import { Component, OnInit, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogContent } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { OrderReturnsService } from '../../../../_services/order-returns.service';
import { AlertService } from '../../../../_services/alert.service';
import { OrderReturn } from '../../../../_interfaces/order-return';
import { returnStatusClass, conditionClass } from '../../status-styles';

@Component({
    selector: 'app-manage-return',
    imports: [CommonModule, FormsModule, MatDialogContent, MatIconModule, MatProgressSpinnerModule, MatTooltipModule, DatePipe],
    templateUrl: './manage-return.component.html',
    styleUrl: './manage-return.component.css'
})
export class ManageReturnComponent implements OnInit {
  readonly dialogRef = inject(MatDialogRef<ManageReturnComponent>);
  readonly data = inject<{ returnId: number }>(MAT_DIALOG_DATA);

  orderReturn: OrderReturn | null = null;
  loading = false;
  savingItemId: number | null = null;
  readonly statusOptions = ['REQUESTED', 'RECEIVED', 'COMPLETED', 'REJECTED'];
  readonly warehouseOptions = ['Kettering', 'Markstey'];
  restockWarehouse: { [itemId: number]: string } = {};
  changed = false;

  returnStatusClass = returnStatusClass;
  conditionClass = conditionClass;

  constructor(
    private orderReturnsService: OrderReturnsService,
    private alertService: AlertService,
  ) {}

  ngOnInit(): void {
    this.load();
  }

  private load(): void {
    this.loading = true;
    this.orderReturnsService.getOrderReturnById(this.data.returnId).subscribe({
      next: (orderReturn) => {
        this.orderReturn = orderReturn;
        (orderReturn.items ?? []).forEach((item) => {
          this.restockWarehouse[item.id] = item.warehouse || 'Kettering';
        });
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        this.alertService.alert(error?.error?.message || 'Failed to load return', 'error');
      },
    });
  }

  get totalQuantity(): number {
    return (this.orderReturn?.items ?? []).reduce((sum, i) => sum + Number(i.quantity), 0);
  }

  get restockableCount(): number {
    return (this.orderReturn?.items ?? []).filter((i) => !i.restocked && i.condition !== 'UNSELLABLE').length;
  }

  get restockedCount(): number {
    return (this.orderReturn?.items ?? []).filter((i) => i.restocked).length;
  }

  statusIcon(status: string): string {
    switch (status) {
      case 'COMPLETED': return 'verified';
      case 'RECEIVED': return 'inventory';
      case 'REJECTED': return 'do_not_disturb_on';
      default: return 'pending_actions';
    }
  }

  updateStatus(status: string): void {
    if (!this.orderReturn || status === this.orderReturn.status) return;
    this.orderReturnsService.updateStatus(this.orderReturn.id, status).subscribe({
      next: (updated) => {
        this.orderReturn = updated;
        this.changed = true;
        this.alertService.alert('Status updated', 'success');
      },
      error: (error) => {
        this.alertService.alert(error?.error?.message || 'Failed to update status', 'error');
      },
    });
  }

  restock(itemId: number): void {
    const warehouse = this.restockWarehouse[itemId];
    if (!warehouse) {
      this.alertService.alert('Choose a warehouse first', 'error');
      return;
    }
    this.savingItemId = itemId;
    this.orderReturnsService.restockItem(itemId, warehouse).subscribe({
      next: () => {
        this.alertService.alert('Item restocked', 'success');
        this.changed = true;
        this.savingItemId = null;
        this.load();
      },
      error: (error) => {
        this.savingItemId = null;
        this.alertService.alert(error?.error?.message || 'Failed to restock item', 'error');
      },
    });
  }

  close(): void {
    this.dialogRef.close({ status: this.changed });
  }
}
